import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { FunnelBars, StatTile, TimeSeries } from '../../components/charts'
import { IconReports } from '../../components/icons'
import { avatarColor, initials } from '../../lib/format'
import { t, tRico } from '../../lib/i18n'
import { useTRPC } from '../../lib/trpc'

export const Route = createFileRoute('/_app/relatorios')({ component: Reports })

/**
 * Os dias são dia-calendário de Brasília, resolvidos no servidor — ver
 * `lib/period.ts`. "Hoje" vai da meia-noite até agora; "Ontem" é um dia
 * fechado, não as últimas 24 horas.
 */
const PERIODOS = [
  // Rótulos em português; traduzidos na hora de desenhar os botões.
  { key: 'hoje' as const, label: 'Hoje' },
  { key: 'ontem' as const, label: 'Ontem' },
  { key: '7d' as const, label: '7 dias' },
  { key: '30d' as const, label: '30 dias' },
  { key: '90d' as const, label: '90 dias' },
]

type PeriodoKey = (typeof PERIODOS)[number]['key']

/** Segundos em algo legível: "3 min", "2 h 10 min". */
function formatDuracao(segundos: number | null): string {
  if (segundos === null) return '—'
  if (segundos < 60) return t('{n} s', { n: Math.round(segundos) })
  if (segundos < 3600) return t('{n} min', { n: Math.round(segundos / 60) })

  const horas = Math.floor(segundos / 3600)
  const minutos = Math.round((segundos % 3600) / 60)
  return minutos ? t('{h} h {m} min', { h: horas, m: minutos }) : t('{h} h', { h: horas })
}

function Reports() {
  const trpc = useTRPC()
  const [period, setPeriod] = useState<PeriodoKey>('30d')
  const periodo = { period }

  const overview = useQuery(trpc.report.overview.queryOptions(periodo))
  const volume = useQuery(trpc.report.volume.queryOptions(periodo))
  const funnel = useQuery(trpc.report.funnel.queryOptions())
  const campaigns = useQuery(trpc.report.campaigns.queryOptions(periodo))
  const agents = useQuery(trpc.report.agents.queryOptions(periodo))
  const response = useQuery(trpc.report.responseTime.queryOptions(periodo))

  const semDados =
    !overview.isPending &&
    (overview.data?.mensagensNoPeriodo ?? 0) === 0 &&
    (overview.data?.abertas ?? 0) === 0

  return (
    <div className="h-full overflow-y-auto">
      <div className="fim-seguro mx-auto max-w-4xl px-5 pt-4 md:pt-8">
        <div className="flex flex-wrap items-baseline gap-3">
          <h1 className="font-display text-2xl">{t('Relatórios')}</h1>
          {/* Filtro numa linha só acima dos gráficos. */}
          <div className="ml-auto flex gap-1">
            {PERIODOS.map((opcao) => (
              <button
                key={opcao.key}
                type="button"
                onClick={() => setPeriod(opcao.key)}
                aria-pressed={period === opcao.key}
                className="rounded-full border border-line bg-surface px-2.5 py-1 text-2xs font-semibold text-ink-2 hover:border-line-strong hover:bg-surface-2 aria-[pressed=true]:border-ink aria-[pressed=true]:bg-ink aria-[pressed=true]:text-paper"
              >
                {t(opcao.label)}
              </button>
            ))}
          </div>
        </div>

        {semDados ? (
          <div className="mt-6 rounded-lg border border-dashed border-line-strong px-4 py-12 text-center">
            <IconReports className="mx-auto size-6 text-ink-3" />
            <p className="mt-2 text-sm text-ink-2">{t('Ainda não há o que medir.')}</p>
            <p className="mx-auto mt-1 max-w-sm text-2xs leading-relaxed text-ink-3">
              {t(
                'Os números aparecem conforme as conversas chegam. O CRM não importa histórico do Telegram, então o relatório começa do zero junto com a primeira conta conectada.',
              )}
            </p>
          </div>
        ) : (
          <>
            <section className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                label={t('Em aberto')}
                value={overview.data?.abertas ?? '—'}
                hint={t('Fora das etapas finais')}
              />
              <StatTile
                label={t('Não lidas')}
                value={overview.data?.naoLidas ?? '—'}
                tone={(overview.data?.naoLidas ?? 0) > 0 ? 'alerta' : undefined}
              />
              <StatTile
                label={t('Sem responsável')}
                value={overview.data?.semResponsavel ?? '—'}
                hint={t('Abertas e sem ninguém atribuído')}
                tone={(overview.data?.semResponsavel ?? 0) > 0 ? 'alerta' : undefined}
              />
              <StatTile
                label={t('Primeira resposta')}
                value={formatDuracao(response.data?.medianaSegundos ?? null)}
                hint={
                  response.data?.amostras
                    ? t('Mediana de {n} conversa(s) · 90% em até {p90}', {
                        n: response.data.amostras,
                        p90: formatDuracao(response.data.p90Segundos),
                      })
                    : t('Nenhuma conversa respondida no período')
                }
              />
            </section>

            {(response.data?.semResposta ?? 0) > 0 && (
              <p
                className="mt-3 rounded-lg px-3 py-2 text-2xs"
                style={{ background: 'var(--st-atend-soft)', color: 'var(--st-atend)' }}
              >
                {tRico(
                  '{n} conversa(s) do período nunca receberam resposta. Elas ficam fora da mediana de propósito — contá-las como zero esconderia justamente o problema.',
                  { n: <strong>{response.data?.semResposta}</strong> },
                )}
              </p>
            )}

            <section className="mt-5">
              <TimeSeries data={volume.data ?? []} />
            </section>

            <section className="mt-5 grid gap-4 lg:grid-cols-2">
              <FunnelBars
                stages={(funnel.data ?? []).map((row) => ({
                  stageKey: row.stageKey,
                  stageLabel: row.stageLabel ? t(row.stageLabel) : t('Sem etapa'),
                  total: row.total,
                }))}
              />

              <figure className="rounded-lg border border-line bg-surface p-3">
                <figcaption className="mb-2.5 text-sm font-semibold">
                  {t('Carga por atendente')}
                </figcaption>
                {(agents.data ?? []).length === 0 ? (
                  <p className="py-4 text-center text-xs text-ink-3">
                    {t('Nenhuma conversa atribuída.')}
                  </p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {(agents.data ?? []).map((row) => (
                      <li key={row.userId} className="flex items-center gap-2.5">
                        <span
                          className="grid size-7 flex-none place-items-center rounded-full text-[10px] font-bold text-white"
                          style={{ background: avatarColor(row.userId) }}
                        >
                          {initials(row.name)}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-xs">{row.name}</span>
                        <span className="flex-none text-2xs text-ink-3">
                          {row.mensagensEnviadas} enviadas
                        </span>
                        <span className="w-8 flex-none text-right text-xs font-semibold tabular-nums">
                          {row.emAberto}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2.5 text-2xs text-ink-3">
                  {t('Número à direita: conversas em aberto na mão do agente.')}
                </p>
              </figure>
            </section>

            <section className="mt-5">
              <figure className="rounded-lg border border-line bg-surface p-3">
                <figcaption className="mb-2.5 text-sm font-semibold">
                  {t('Origem por campanha')}
                </figcaption>

                {(campaigns.data ?? []).length === 0 ? (
                  <p className="py-4 text-center text-xs text-ink-3">
                    {t('Nenhuma campanha cadastrada.')}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="text-2xs uppercase tracking-wider text-ink-3">
                        <tr>
                          <th className="pb-1.5 font-semibold">{t('Campanha')}</th>
                          <th className="pb-1.5 font-semibold">{t('Conta')}</th>
                          <th className="pb-1.5 text-right font-semibold">{t('Leads')}</th>
                          <th className="pb-1.5 text-right font-semibold">{t('Exatos')}</th>
                          <th className="pb-1.5 text-right font-semibold">{t('Já existentes')}</th>
                          <th className="pb-1.5 text-right font-semibold">{t('Concluídos')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(campaigns.data ?? []).map((row) => (
                          <tr key={row.campaignId} className="border-t border-line">
                            <td className="py-1.5 font-semibold">{row.label}</td>
                            <td className="py-1.5 text-ink-2">{row.channelName}</td>
                            <td className="py-1.5 text-right tabular-nums">{row.total}</td>
                            <td className="py-1.5 text-right tabular-nums text-ink-2">
                              {row.exatas ?? 0}
                            </td>
                            <td className="py-1.5 text-right tabular-nums text-ink-2">
                              {row.jaExistentes ?? 0}
                            </td>
                            <td className="py-1.5 text-right tabular-nums text-ink-2">
                              {row.concluidas ?? 0}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <p className="mt-2.5 text-2xs leading-relaxed text-ink-3">
                  {/* "payload" não aparece na tela: o que importa para quem lê
                      é que o link não se adultera. */}
                  {tRico(
                    '{exatos} são os leads que chegaram pelo link de {start}, que o cliente não consegue alterar. O restante veio de conta business, onde a origem é {inferida} pelo texto da primeira mensagem e pode errar. {jaExistentes} são os que chegaram por esta campanha mas já estavam no CRM: a origem de um lead é a primeira e não muda, então eles não entram na coluna de leads.',
                    {
                      exatos: <strong>{t('Exatos')}</strong>,
                      start: <code>/start</code>,
                      inferida: <em>{t('inferida')}</em>,
                      jaExistentes: <strong>{t('Já existentes')}</strong>,
                    },
                  )}
                </p>
              </figure>
            </section>
          </>
        )}
      </div>
    </div>
  )
}
